import { Pagination } from '@heroui/react'

// Page numbers under a list, with "1–50 of 312" beside them.
//
// Controlled: the caller owns `page` and fetches that page. Pages are 1-based.
// Nothing renders while the list is empty, and the numbers hide when everything
// fits on one page, so a short list shows only its range.
//
// The range text is the caller's to translate. This package has no locale
// files, so `rangeLabel` receives the numbers and returns the sentence:
//   rangeLabel={(from, to, total) => t('common.pageRange', { from, to, total })}
// Without it the text is language-neutral: "1–50 / 312".

export interface PaginationBarProps {
  /** The current page, from 1. */
  page: number
  pageSize: number
  /** How many items the whole list holds, across every page. */
  total: number
  onPageChange: (page: number) => void
  rangeLabel?: (from: number, to: number, total: number) => string
  /** While the next page loads: keeps the bar in place but stops repeat clicks. */
  isDisabled?: boolean
  /** Names the control for screen readers, e.g. "Activity pages". */
  ariaLabel?: string
  className?: string
}

/** The number of pages a list of `total` needs, never less than one. */
export function pageCount(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize))
}

/** The item offset of a 1-based page, for an API that takes `offset`. */
export function pageOffset(page: number, pageSize: number): number {
  return (Math.max(1, page) - 1) * pageSize
}

export function PaginationBar({
  page,
  pageSize,
  total,
  onPageChange,
  rangeLabel,
  isDisabled = false,
  ariaLabel,
  className,
}: PaginationBarProps) {
  if (total <= 0) return null

  const pages = pageCount(total, pageSize)
  const current = Math.min(Math.max(1, page), pages)
  const from = pageOffset(current, pageSize) + 1
  const to = Math.min(current * pageSize, total)
  const label = rangeLabel ? rangeLabel(from, to, total) : `${from}–${to} / ${total}`

  return (
    <div
      className={`flex flex-col items-center gap-2 sm:flex-row sm:justify-between ${className ?? ''}`}
    >
      <p className="text-sm text-foreground/60" aria-live="polite">
        {label}
      </p>
      {pages > 1 && (
        <Pagination
          aria-label={ariaLabel}
          page={current}
          total={pages}
          onChange={onPageChange}
          isDisabled={isDisabled}
          showControls
          isCompact
          size="sm"
        />
      )}
    </div>
  )
}
